import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { getPreview } from "api/creditnote.service";
import { logError } from "api/diagnostic.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
} from "common/constants";
import {
  escapeNewLine,
  formatDate,
  formatNZD,
  formatPercent,
} from "common/utils";
import React, { useEffect, useState } from "react";
import type { PreviewResponseCreditNote } from "types/model.types";

/**
 * Props for the AdminCreditInvoicePreview component.
 *
 * @interface IPreviewProps
 * @property {number} id - Unique numeric identifier for the invoice to display in the preview. This typically corresponds to the invoice record's primary key and is required to fetch or render the correct invoice data.
 */
interface IPreviewProps {
  id: number;
}
/**
 * AdminCreditInvoicePreview
 *
 * React functional component that renders a printable/ exportable preview of a credit note.
 *
 * This component:
 * - Accepts a single prop `id` and fetches preview data for that credit note when `id` is provided.
 * - Uses an internal ref to prevent duplicate API calls while a fetch is in-flight.
 * - Renders header, debtor information, invoice meta (IDs, date, GST), line items, and totals.
 * - Provides UI controls to Print or Export to PDF. While exporting, the export button is disabled and a loader is shown.
 *
 * Data fetching and state:
 * - Calls getPreview(id) to retrieve preview data (expected shape: PreviewResponseCreditNote).
 * - Stores the result in state via setPreviewData.
 * - On fetch failure the error is logged and a user notification is shown.
 *
 * Print / Export behavior:
 * - Both print and export routines hide any DOM elements that match the `.no-print` selector before capturing/printing.
 * - The preview content that is captured/printed must be the element with id="preview-content".
 * - exportToPDF captures the preview content using html2canvas, converts it to an image, inserts that image into a jsPDF A4 document
 *   (using PRINT_MARGIN to calculate usable width), then saves the file as `Order_{id}.pdf`. Notifications are shown on success/failure.
 * - handlePrint calls printPage with the preview's innerHTML to trigger browser printing.
 *
 * DOM / CSS expectations:
 * - The preview area must have id="preview-content".
 * - Elements that should not appear in print/export should have the CSS class `no-print`.
 * - table cells that contain monetary values use the `money` / `money-focal` classes for styling/alignment.
 *
 * External dependencies / helper utilities used (must be present in the surrounding codebase):
 * - getPreview(id): Promise<PreviewResponseCreditNote>
 * - logError(err)
 * - notifications (show)
 * - html2canvas
 * - jsPDF
 * - PRINT_MARGIN (number)
 * - printPage(html: string)
 * - formatNZD, formatDate, escapeNewLine
 *
 * Accessibility & UX notes:
 * - While exporting, the component shows a loader and disables the export button to prevent repeated interactions.
 * - Errors during fetch or export display toast-style notifications to the user.
 *
 * @param props.id - The identifier of the credit note to preview. When provided, the component will fetch and render the invoice preview.
 * @returns A React element that displays the credit note preview and provides print/export controls.
 *
 * @example
 * <AdminCreditInvoicePreview id="INV-12345" />
 *
 * @remarks
 * - The component assumes the preview data shape contains `debtorInfo`, `summary`, and `entries`. Each entry is expected to have
 *   fields like `id`, `productId`, `productCode`, `description`, `returned`, `unitPrice`, `discount`, and `total`.
 * - Ensure CSS and helper utilities referenced above are available in the environment where this component is used.
 */
const AdminCreditInvoicePreview: React.FC<IPreviewProps> = ({ id }) => {
  const [previewData, setPreviewData] =
    useState<PreviewResponseCreditNote | null>(null);

  useEffect(() => {
    if (!id) return;

    let mounted = true;

    const fetchData = async () => {
      try {
        const data = await safeApiCall(getPreview(id));
        if (!mounted) return;

        setPreviewData(data ?? null);
      } catch (err) {
        logError(err);
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_UPDATE,
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <>
      <div className="sub-title">*** Credit Note ***</div>
      <table className="print-table padTop">
        <tbody>
          <tr>
            <td style={{ width: "50%" }} className="leftCol">
              <span className="heading">Invoice To</span>
              <div className="divider" />
              <div
                dangerouslySetInnerHTML={{
                  __html: escapeNewLine(
                    [
                      previewData?.debtorInfo.debtorName,
                      previewData?.debtorInfo.addressLine1,
                      previewData?.debtorInfo.addressLine2,
                      previewData?.debtorInfo.addressLine3,
                    ]
                      .filter(Boolean)
                      .join("\n"),
                  ),
                }}
              />
            </td>
            <td className="rightCol">
              <div>
                <span className="rcl">Date:</span>
                <span className="rcv">
                  {formatDate(
                    previewData?.summary.createdOn ?? new Date().toDateString(),
                  )}
                </span>
              </div>
              <div>
                <span className="rcl">Tax Credit Note No:</span>
                <span className="rcv">{previewData?.summary.id}</span>
              </div>
              <div>
                <span className="rcl">Tax Invoice No:</span>
                <span className="rcv">{previewData?.summary.invoiceId}</span>
              </div>
              <div>
                <span className="rcl">Customer No:</span>
                <span className="rcv">{previewData?.debtorInfo.id}</span>
              </div>
              <div>
                <span className="rcl">Gst:</span>
                <span className="rcv">
                  {process.env.REACT_APP_GST_NO ?? "20-898-763"}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="spacer padTop padBottom" />

      {/* Render logic for orderDetails array here */}
      <table className="print-table details-table">
        <thead>
          <tr>
            <th className="nowrap">Product</th>
            <th className="description">Description</th>
            <th className="nowrap">Quan</th>
            <th className="money">Price</th>
            <th className="money">Dis %</th>
            <th className="money">Total</th>
          </tr>
        </thead>

        <tbody>
          {previewData?.summary.notes && (
            <tr>
              <td></td>
              <td colSpan={5} className="description">
                {previewData.summary.notes}
              </td>
            </tr>
          )}
          {previewData?.entries.map((item) => (
            <tr key={item.id}>
              <td>{`${item.productId}${item.productCode}`}</td>
              <td>{item.description}</td>
              <td>{item.returned ?? 0}</td>
              <td className="money">{formatNZD(item.unitPrice ?? 0)}</td>
              <td className="money">
                {item.discount > 0 ? formatPercent(item.discount) : ""}
              </td>
              <td className="money">{formatNZD(item.total ?? 0)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={6}>
              <div style={{ height: "20px" }}></div>
            </td>
          </tr>
          <tr>
            <td colSpan={4} />
            <td className="money">Total</td>
            <td className="money">
              {formatNZD(previewData?.summary.amount ?? 0)}
            </td>
          </tr>
          <tr>
            <td colSpan={4} />
            <td className="money">GST</td>
            <td className="money">
              {formatNZD(previewData?.summary.gst ?? 0)}
            </td>
          </tr>
          <tr>
            <td colSpan={4} />
            <td className="money money-focal">TOTAL</td>
            <td className="money money-focal">
              {formatNZD(previewData?.summary.grandTotal ?? 0)}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default AdminCreditInvoicePreview;
